<template>
  <div>
    <LoaderIndicator v-if="isLoading" with-container />
    <div>
      <ConclusionStep
        status="positive"
        :title="title"
        :description="description"
        @close="() => onBack(true)"
      >
        <ContentDisplay
          :blocks-length="1"
          content-aspect="outside"
          footer-type="caption"
          footer-label="Produtos que estiverem fora do horário de movimentação do mercado serão agendados para o próximo dia útil disponível. Garanta que o valor do investimento esteja disponível na sua Conta Digital para que estas operações sejam realizadas."
        >
          <template #content-block-1>
            <HeadingDisplay
              class="title"
              title="Movimentação"
              size="extra-small"
            />
            <ListDisplay
              class="list-item"
              has-divider
              title="Origem"
              value="Conta Digital"
            />
            <ListDisplay
              class="list-item"
              has-divider
              title="Destino"
              :value="portfolioName"
            />
            <ListDisplay
              v-for="product in products"
              :key="product.name"
              class="list-item"
              has-divider
              :title="product.name"
              :value="product.value"
            />
            <ListDisplay
              has-divider
              title="Valor total investido"
              :value="totalAmount"
            />
          </template>
        </ContentDisplay>
        <template #button-primary>
          <ButtonPrimary
            size="large"
            label="Continuar"
            @click="onBack(false)"
          />
        </template>
      </ConclusionStep>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import {
  ConclusionStep,
  ContentDisplay,
  ListDisplay,
  ButtonPrimary,
  LoaderIndicator,
  HeadingDisplay
} from '@warrenbrasil/nebraska-web';
import { IPortfolioInvestmentSummary } from '@/modules/wealth/store/investments';
import {
  trackLoadSuccess,
  trackCloseButton,
  trackInvestOtherWallet,
  trackBackToWealth
} from './track-events';
import { isImpersonate } from '@/modules/common/services/partner';
import {
  getUserB2B,
  StorageKeys
} from '@/modules/common/services/local-storage';
import { B2bPartnerType } from '@/types/models/Customer/AdvisorProfile';

import { WarrenSession } from '@/modules/common/services/session';

const InvestmentsModule = namespace('InvestmentsModule');

@Component({
  name: 'InvestmentCompleted',
  components: {
    LoaderIndicator,
    ConclusionStep,
    ContentDisplay,
    ListDisplay,
    ButtonPrimary,
    HeadingDisplay
  }
})
export default class InvestmentCompleted extends Vue {
  @InvestmentsModule.Getter('investmentCompletedSummary')
  private investmentSummary!: IPortfolioInvestmentSummary;

  private isLoading = true;

  onBack(backButton = false) {
    if (backButton) {
      trackCloseButton(this.isScheduled);
    } else {
      trackBackToWealth(this.isScheduled);
    }
    this.$router.push({
      name: 'wealthPortfolio',
      params: {
        id: this.investmentSummary.portfolioApiId,
        type: this.$route.query.type as string
      }
    });
  }

  onInvestButton() {
    trackInvestOtherWallet(this.isScheduled);
    this.$router.push({ name: 'wealthInvest' });
  }

  get isScheduled() {
    return !!this.$route.query.scheduled;
  }

  get isPartnerManager(): boolean {
    const partner = getUserB2B();
    return partner?.advisorProfile?.type === B2bPartnerType.MANAGER;
  }

  get title() {
    return !this.showSuggestionMessage
      ? this.scheduledTitle
      : 'Sugestão de investimento enviada';
  }

  get scheduledTitle() {
    return this.isScheduled
      ? 'Investimento agendado'
      : 'Investimento solicitado';
  }

  get description() {
    const managerDescription = this.isPartnerManager
      ? null
      : 'A aprovação do seu cliente pela plataforma é necessária para a conclusão do investimento.';
    return !this.showSuggestionMessage
      ? this.scheduledDescription
      : managerDescription;
  }

  get scheduledDescription() {
    return this.isScheduled
      ? WarrenSession.get(StorageKeys.ScheduleInvestmentMessage)
      : 'Em breve você poderá visualizar os rendimentos.';
  }

  get showSuggestionMessage() {
    return isImpersonate();
  }

  get portfolioName() {
    return this.investmentSummary.portfolioName;
  }

  get totalAmount() {
    return this.investmentSummary.total.formatted;
  }

  get products() {
    return this.investmentSummary.products.map(product => ({
      name: product.name,
      value: product.amount.formatted
    }));
  }

  mounted() {
    const messageTracking = this.showSuggestionMessage
      ? 'Sugestão de investimento enviada'
      : 'Investimento concluído';
    trackLoadSuccess(messageTracking, this.isScheduled);

    this.isLoading = false;
  }
}
</script>
<style lang="less" scoped>
.list-item {
  margin-bottom: @size-spacing-x400;
}

.title {
  margin-bottom: @size-spacing-x500;
}
</style>
